import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lightbulb, Settings, Mail, Rocket } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/7514814/pexels-photo-7514814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const STEPS = [
  {
    num: "01",
    icon: Lightbulb,
    title: "Product Blueprint",
    desc: "We audit your audience, identify high-demand topics, and craft a digital product concept that your followers actually want to buy.",
  },
  {
    num: "02",
    icon: Settings,
    title: "Tech Stack Setup",
    desc: "Payment processing, course hosting, membership platforms. We build the entire backend infrastructure so you can focus on creating.",
  },
  {
    num: "03",
    icon: Mail,
    title: "Email Ecosystem",
    desc: "Lead magnets, welcome sequences, nurture campaigns. We engineer an email system that converts cold followers into paying customers.",
  },
  {
    num: "04",
    icon: Rocket,
    title: "Launch Campaign",
    desc: "Strategic pre-launch, cart open/close sequences, affiliate partnerships. We orchestrate a launch that maximizes first-week revenue.",
  },
];

export default function ServiceCreator() {
  return (
    <div>
      {/* ========== HERO (DARK) ========== */}
      <section
        data-testid="creator-hero-section"
        className="relative min-h-[80vh] flex items-center bg-dark_base overflow-hidden"
      >
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark_base/70 via-dark_base/50 to-dark_base" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-6 font-body">
              Creator Monetization
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] tracking-tight font-extrabold text-cream mb-8">
              Your Audience is Ready to{" "}
              <span className="font-accent italic font-normal text-mint">Buy.</span>{" "}
              Are You Ready to{" "}
              <span className="font-accent italic font-normal text-mint">Sell?</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg md:text-xl leading-relaxed text-cream/70 max-w-2xl font-body">
              Stop leaving money on the table with brand deals alone. We build backend monetization systems that turn your influence into a scalable business.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== PROCESS (LIGHT) ========== */}
      <section className="bg-light_base -mt-12 rounded-t-[3rem] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Our Process
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-4xl md:text-5xl leading-tight tracking-tight font-bold text-dark_base mb-6">
              From Followers to <span className="font-accent italic font-normal text-mint">Revenue</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="font-body text-base md:text-lg text-dark_base/60 max-w-2xl mb-20">
              A proven 4-step system that transforms your audience into a reliable income stream.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-testid="creator-process-grid">
            {STEPS.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                data-testid={`process-step-${step.num}`}
                className="bg-white border border-dark_base/5 rounded-3xl p-8 md:p-10 group hover:shadow-xl transition-all duration-500"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <span className="font-accent italic text-6xl md:text-7xl text-mint/20 font-semibold leading-none">
                      {step.num}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <step.icon size={22} className="text-mint" />
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-dark_base">
                        {step.title}
                      </h3>
                    </div>
                    <p className="font-body text-sm md:text-base leading-relaxed text-dark_base/60">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ========== WHAT CREATORS GET ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="bg-dark_surface rounded-3xl p-10 md:p-16"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Results
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl md:text-4xl leading-tight tracking-tight font-bold text-cream mb-12">
              What Our Creators <span className="font-accent italic font-normal text-mint">Achieve</span>
            </motion.h2>
            <motion.div variants={fadeUp} custom={2} className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {[
                { value: "$10k+", label: "Average Launch Revenue" },
                { value: "40%", label: "Email Open Rate" },
                { value: "5x", label: "ROI on Setup Cost" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="font-heading text-4xl md:text-5xl font-extrabold text-mint mb-2">{stat.value}</p>
                  <p className="font-body text-cream/60 text-sm">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* ========== CTA ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-dark_base rounded-3xl p-12 md:p-20 text-center"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Let's Talk Strategy
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight font-bold text-cream mb-4">
              Ready to Monetize Your{" "}
              <span className="font-accent italic font-normal text-mint">Influence?</span>
            </h2>
            <p className="font-body text-cream/60 text-base md:text-lg max-w-xl mx-auto mb-10">
              Book a free strategy call. We'll map out your product, tech stack, and launch timeline in 30 minutes.
            </p>
            <Link
              to="/contact"
              data-testid="creator-strategy-cta"
              className="inline-flex bg-mint text-dark_base font-bold py-4 px-10 rounded-full hover:bg-mint-hover transition-colors duration-300 font-body items-center gap-2"
            >
              Book a Monetization Strategy Call <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
